<template>
  <v-dialog :value="true" max-width="450px" :hide-overlay="true" :persistent="true">
    <v-card>
      <v-card-title class="headline">{{ $t('sign-in') }}</v-card-title>
      <v-card-text v-if="disabled_account" data-cy="reactivate-block">
        <a-loader v-show="loading !== ''" :enabled="true" :text="loading" style="margin: 40px;"></a-loader>
        <span v-show="loading === ''">{{ $t('reactivate') }}</span>
      </v-card-text>
      <v-card-text v-else>
        <a-loader v-show="loading !== ''" :enabled="true" :text="loading" style="margin: 40px;"></a-loader>
        <v-form v-show="loading === ''" v-model="form.valid" class="mt-4">
          <v-text-field
              autofocus
              v-model="form.fields.email"
              @keyup.enter.prevent="submit"
              :label="$t('field.email')"
              data-cy="email-input"
              required>
          </v-text-field>
          <v-text-field
              v-model="form.fields.password"
              @keyup.enter.prevent="submit"
              :label="$t('field.password')"
              type="password"
              data-cy="password-input"
              required>
          </v-text-field>
          <div style="text-align: right;">
            <a @click="openResetPassword">{{ $t('forgot-password') }}</a>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn v-if="disabled_account" color="blue darken-1" text @click="submit" data-cy="submit-button">
          {{ $t('actions.reactivate') }}
        </v-btn>
        <v-btn v-else color="blue darken-1" text @click="submit" data-cy="submit-button">
          {{ $t('actions.connection') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { get } from 'lodash'

export default {
  name: 'ALoginWidget',
  data () {
    return {
      loading: '',
      disabled_account: false,
      form: {
        valid: false,
        fields: {
          email: '',
          password: ''
        }
      }
    }
  },
  methods: {
    async authenticate ({ email, password, token }) {
      this.loading = this.$t('loading.authenticating')

      try {
        await this.$recaptchaLoaded()
        await this.$account.authenticate({
          email,
          password,
          token,
          _passive_client_token: await this.$recaptcha('login_credentials'),
          resurrect: this.disabled_account ? true : undefined
        }, {
          onProgress: message => {
            this.loading = this.$t('loading.' + message)
          }
        })
        this.loading = this.$t('loading.opening-application')

        await this.$router.push({ name: 'home' })
      } catch (err) {
        if (get(err, 'data.exception') === 'AccountDisabledException') {
          this.disabled_account = true
        }
      } finally {
        this.loading = ''
      }
    },
    async submit () {
      await this.authenticate(this.form.fields)
    },
    openResetPassword () {
      this.$router.push({ name: 'forgot_password' })
    },
  },
  async mounted () {
    // If the guy is already authenticated, exit the login
    if (this.$account.isAuthenticated) {
      await this.$router.push({ name: 'home' })
    }

    // If we have a manual token in the url, try authenticating with it
    if (this.$route.params.token) {
      await this.authenticate({ token: this.$route.params.token })
    }

    if (process.env.VUE_APP_DEFAULT_EMAIL) this.form.fields.email = process.env.VUE_APP_DEFAULT_EMAIL
    if (process.env.VUE_APP_DEFAULT_PASSWORD) this.form.fields.password = process.env.VUE_APP_DEFAULT_PASSWORD
  }
}
</script>

<i18n>
{
	"en": {
		"field": {
			"email": "Email",
			"password": "Password"
		},
		"forgot-password": "Forgot password ?",
		"loading": {
			"authenticating": "Auhenticating ...",
			"getting-token": "Retrieving login token...",
			"loading-account": "Loading account data...",
			"opening-application": "Launching the app..."
		},
		"reactivate": "Your account is currently deactivated, do you want to reactivate it to connect to it?",
		"sign-in": "Sign in"
	},
	"es": {
		"field": {
			"email": "e-mail",
			"password": "Contraseña"
		},
		"forgot-password": "Contraseña olvidada ?",
		"loading": {
			"authenticating": "Autenticando...",
			"getting-token": "Recuperando token de inicio de sesión...",
			"loading-account": "Cargando datos de la cuenta...",
			"opening-application": "Iniciando la aplicación..."
		},
		"reactivate": "Tu cuenta está actualmente desactivada, ¿quieres reactivarla para conectarte a ella?",
		"sign-in": "acceder"
	},
	"fr": {
		"field": {
			"email": "Email",
			"password": "Mot de passe"
		},
		"forgot-password": "Mot de passe oublié ?",
		"loading": {
			"authenticating": "Connexion en cours ...",
			"getting-token": "Récupération du jeton de connexion ...",
			"loading-account": "Chargement des données du compte ...",
			"opening-application": "Lancement de l'application ..."
		},
		"reactivate": "Votre compte est actuellement désactivé, souhaitez-vous le réactiver pour vous y connecter ?",
		"sign-in": "Connexion"
	},
	"it": {
		"field": {
			"email": "Email",
			"password": "Password"
		},
		"forgot-password": "Password dimenticata ?",
		"loading": {
			"authenticating": "Accesso in corso ...",
			"getting-token": "Recupero del token di accesso ...",
			"loading-account": "Caricamento dei dati dell'account ...",
			"opening-application": "Avvio dell'applicazione ..."
		},
		"reactivate": "Il tuo account è attualmente disattivato, vuoi riattivarlo per accedervi?",
		"sign-in": "Accedi"
	},
	"pt": {
		"field": {
			"email": "Email",
			"password": "Palavra-passe"
		},
		"forgot-password": "Esqueceu a palavra-passe ?",
		"loading": {
			"authenticating": "A autenticar ...",
			"getting-token": "A obter token de acesso ...",
			"loading-account": "A carregar dados da conta ...",
			"opening-application": "A iniciar a aplicação ..."
		},
		"reactivate": "A sua conta está atualmente desativada, deseja reativá-la para aceder?",
		"sign-in": "Iniciar sessão"
	},
	"pl": {
		"field": {
			"email": "Email",
			"password": "Hasło"
		},
		"forgot-password": "Zapomniałeś hasła?",
		"loading": {
			"authenticating": "Logowanie ...",
			"getting-token": "Pobieranie tokenu logowania ...",
			"loading-account": "Ładowanie danych konta ...",
			"opening-application": "Uruchamianie aplikacji ..."
		},
		"reactivate": "Twoje konto jest aktualnie zdezaktywowane, czy chcesz je reaktywować, aby się zalogować?",
		"sign-in": "Zaloguj się"
	}
}
</i18n>
